import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { BlogLayout, useHead } from "../components/blog/BlogLayout";
import { API, mediaUrl, fmtDate, SOURCE_LABEL, LANG_LABEL, HTML_LANG } from "../lib/blogApi";
import { NewsletterSignup, NewsletterStatus } from "../components/blog/Newsletter";

function FilterSelect({ id, label, value, onChange, options }) {
  return (
    <label htmlFor={id} className="flex flex-col gap-1 font-mono text-[12px] tracking-[0.12em] uppercase">
      {label}
      <select id={id} data-testid={id} value={value} onChange={(e) => onChange(e.target.value)}
        className="min-w-[160px] bg-paper border border-ink px-3 py-2 font-sans normal-case tracking-normal text-[15px]">
        <option value="">Alle</option>
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </label>
  );
}

function PostCard({ p }) {
  const img = p.cover_media_id ? mediaUrl(p.cover_media_id) : p.cover_url;
  const external = p.source !== "own";
  return (
    <li data-testid="blog-card" lang={HTML_LANG[p.lang]} className="border-t border-ink pt-6 grid md:grid-cols-[1fr_220px] gap-6">
      <div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 font-mono text-[12px] tracking-[0.1em] uppercase text-[#4a4a4a]">
          <time dateTime={p.published_at}>{fmtDate(p.published_at, p.lang)}</time>
          {p.reading_min && <><span aria-hidden="true">·</span><span>{p.reading_min} min</span></>}
          <span className={`px-2 py-0.5 border ${external ? "border-[#7a2d00] text-[#7a2d00]" : "border-ink"}`}>
            {external ? `fra ${p.source_name}` : "Egne tanker"}
          </span>
        </div>
        <h2 className="mt-3 font-serif text-3xl leading-tight">
          <Link to={`/blog/${p.slug}`} data-testid={`blog-card-link-${p.slug}`} className="hover:text-[#7a2d00] underline-offset-4 hover:underline">
            {p.title}
          </Link>
        </h2>
        {p.excerpt && <p className="mt-3 text-[17px] leading-relaxed max-w-[62ch]">{p.excerpt}</p>}
        {p.tags?.length > 0 && (
          <ul className="mt-3 flex flex-wrap gap-2" aria-label="Tagger">
            {p.tags.map((t) => <li key={t} className="font-mono text-[12px] text-[#4a4a4a]">#{t}</li>)}
          </ul>
        )}
      </div>
      {img && <img src={img} alt="" loading="lazy" onError={(e) => { e.currentTarget.style.display = "none"; }} className="w-full h-40 md:h-36 object-cover border border-ink" />}
    </li>
  );
}

export default function BlogIndex() {
  const [posts, setPosts] = useState(null);
  const [facets, setFacets] = useState({ tags: [], sources: [], langs: [] });
  const [f, setF] = useState(() => {
    const q = new URLSearchParams(window.location.search);
    return { lang: q.get("lang") || "", source: q.get("source") || "", tag: q.get("tag") || "" };
  });
  useHead({ title: "Blogg — Nickolass Jensen", description: "Tanker om SEO, AI-synlighet (3LA) og sprint — og nytt fra Nettpilot, 3LA og Elite Sprint Trening.",
    canonical: "https://nickolass.com/blog", alternate: `${API}/blog`, lang: "no" });

  useEffect(() => { axios.get(`${API}/blogdata/facets`).then(({ data }) => setFacets(data)).catch(() => {}); }, []);
  useEffect(() => {
    const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
    axios.get(`${API}/blogdata/posts`, { params }).then(({ data }) => setPosts(data)).catch(() => setPosts([]));
  }, [f]);

  return (
    <BlogLayout>
      <div className="mx-auto max-w-[1100px] px-5 md:px-8 pt-16 md:pt-24">
        <p className="font-mono text-[12px] tracking-[0.22em] uppercase text-[#7a2d00]">Nickolass Jensen · Blogg</p>
        <h1 className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.02] max-w-[18ch]">Tanker, notater og nytt fra mine nettsteder.</h1>
        <p className="mt-6 max-w-[60ch] text-lg leading-relaxed">
          SEO, AI-synlighet etter Three-Layer Approach™ og sprint. Egne innlegg — og korte sammendrag av det jeg publiserer andre steder, med lenke til originalen.
        </p>
        <NewsletterStatus />
        <div className="mt-12 flex flex-wrap gap-4" role="search" aria-label="Filtrer innlegg">
          <FilterSelect id="blog-filter-lang" label="Språk" value={f.lang} onChange={(v) => setF({ ...f, lang: v })}
            options={facets.langs.map((l) => [l, LANG_LABEL[l] || l])} />
          <FilterSelect id="blog-filter-source" label="Kilde" value={f.source} onChange={(v) => setF({ ...f, source: v })}
            options={facets.sources.map((s) => [s, SOURCE_LABEL(s)])} />
          <FilterSelect id="blog-filter-tag" label="Tagg" value={f.tag} onChange={(v) => setF({ ...f, tag: v })}
            options={facets.tags.map((t) => [t, `#${t}`])} />
        </div>
        <p className="sr-only" aria-live="polite">{posts ? `${posts.length} innlegg` : ""}</p>
        {posts === null && <p className="mt-12">Laster innlegg…</p>}
        {posts?.length === 0 && <p data-testid="blog-empty" className="mt-12">Ingen innlegg matcher filteret ennå.</p>}
        <ul className="mt-12 space-y-10" data-testid="blog-list">{posts?.map((p) => <PostCard key={p.id} p={p} />)}</ul>
        <div className="mt-16"><NewsletterSignup /></div>
      </div>
    </BlogLayout>
  );
}
